import _ from 'lodash';

import {
  Component,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  ElementRef,
  Inject,
  DOCUMENT
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MembersService } from 'src/app/shared/services/members.service';
import { ApplicationService } from 'src/app/shared/services/application.service';
import { CustomValidators } from 'src/app/shared/validators/custom.validator';
import { NotEmptyValidator } from 'src/app/shared/validators/not-empty.validator';
import { Image } from 'src/app/people/models/image';
import { photoRequiredMessage } from 'src/shared-app-portal/components/cloudinary-upload/cloudinary-upload.component';
import {
  LookupService,
  StateCountryLookupService
} from 'src/shared-app-portal/services/lookup.service';
import { PersonApplicationWrite } from 'src/app/people/models/personApplicationWrite.model';
import { FileItem } from 'ng2-file-upload';
import { Race } from 'src/app/shared/models/race';
import { FormFieldName } from 'src/app/shared/models/application/formFieldName';
import { Application } from 'src/app/shared/models/application/application';
import { Gender } from 'src/app/shared/models/gender';
import { Form } from 'src/app/shared/models/application/form';
import { MembersResumeService } from 'src/app/shared/services/members-resume.service';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { BuildInfoCheckService } from 'src/app/shared/services/build-info-check.service';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { PositionPersonPickerComponent } from './position-person-picker/position-person-picker.component';
import { AttributeWrite } from 'src/app/shared/models/attributes/attributeWrite';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import {
  AnswerModel,
  LocalData,
  LocalPersistenceService
} from 'src/shared-app-portal/services/local-persistence.service';
import { DocumentScanEvent } from 'src/app/shared/models/documentScanEvent';
import { DocumentScanStatusType } from 'src/app/shared/models/documentScanStatusType';
import { AttributeEditorMode } from 'src/app/shared/models/attributes/attributeEditorMode';
import {
  FieldValidatorTracker,
  getAllControlErrors,
  getFailedValidationMessage
} from 'src/app/shared/models/form/field-validator-tracker';
import { PersonForMemberRequests } from 'src/app/people/models/personForApplication';
import { ApplicationFormPage } from 'src/app/shared/models/application/formPage';
import { ApplicationPaymentComponent } from '../application-payment/application-payment.component';
import { SnackBarService } from 'src/app/shared/services/snackbar.service';
import {
  AddressHelper,
  AddressHelperFactory
} from 'src/shared-app-portal/services/address-helper.service';
import {
  getHiddenAndRequired,
  isMailingAddressVisible
} from 'src/app/shared/models/application/formField';
import { PaymentResult } from 'src/app/shared/models/application/paymentOptions';
import { SubmitApplicationResultModel } from 'src/app/shared/models/application/submitApplicationResultModel';
import { getParsedError } from 'src/app/shared/services/error-handler-factory.service';
import {
  buildApplicationSubmit,
  buildPaymentPreview
} from 'src/app/shared/models/application/applicationSubmit';
import { cleanPhoneNumber } from 'src/shared-app-portal/components/phone/phone.component';
import { ApplicationFieldName } from 'src/app/shared/models/application-definition/application-field';
import { PortalAndLineupApplicationConnectionService } from 'src/shared-app-portal/services/portal-and-lineup-application-connection.service';
import { AttributesEditorDataSource } from 'src/shared-app-portal/components/attributes-editor/attributesEditorDataSource';
import { AttributesEditorComponent } from 'src/shared-app-portal/components/attributes-editor/attributes-editor.component';
import { AccessibilityService } from 'src/app/shared/services/accessibility.service';
import { TeamNameUniqueAsyncValidator } from 'src/app/shared/validators/team-name-unique.async-validator';
import { PersonEmailModel } from '../../email-editor/person-email.model';

const empty: Race[] = [];
/**
 * Shared so the seat field binding keeps a stable reference. Returning a fresh `{}` marked the input
 * as changed on every check, re-entering the picker's ngOnChanges for no reason.
 */
const noSeatFieldValues: Readonly<Record<string, Attribute[]>> = Object.freeze({});
export const blankApplication = (): Application => {
  var application = new Application();
  application.form = new Form();
  application.pages = [];
  return application;
};
type teamForm = {
  teamName: FormControl<string>;
  teamPublicName: FormControl<string>;
};
type personForm = {
  phoneNumber: FormControl<string>;
  extension: FormControl<string>;
  phoneNumberRaw: FormControl<string>;
  firstName: FormControl<string>;
  prefix: FormControl<string>;
  suffix: FormControl<string>;
  lastName: FormControl<string>;
  middleName: FormControl<string>;
  email: FormControl<string>;
  jobTitle: FormControl<string>;
  company: FormControl<string>;
  linkedInProfile: FormControl<string>;
  gender: FormControl<string>;
  races: FormControl<Race[]>;
  resumeUpload: FormControl<string>;
  /** holds the uploaded photo's id so a required profile photo validates like any other field */
  avatar: FormControl<string>;
};
@Component({
  selector: 'app-application',
  templateUrl: './application.component.html',
  styleUrls: ['./application.component.scss'],
  standalone: false
})
export class ApplicationComponent implements OnInit, OnDestroy {
  get inPreviewMode() {
    return !!this.onPreviewSubmit;
  }
  @Input()
  previewFormId: string;
  @Input()
  onPreviewSubmit: Function;
  @Input()
  previewPageId: string;
  get isSinglePagePreview(): boolean {
    return this.inPreviewMode && !!this.previewPageId;
  }
  private routeSubscription;
  private racesSubscription;
  totalPages: number = 1;
  /** will never be 0 when in use (first). Will go away once positions are integrated more into pages */
  positionsPageIndex: number = 0;
  /** dictionary of EditPositionOnApplication ids containing a list of personIds to assign to the person */
  selectedPositionPersonIds: Record<string, string[]> = {};
  /** positionDefinitionId -> personId -> seat (tracked) field values collected on the form */
  selectedPositionSeatFieldValues: Record<string, Record<string, AttributeWrite[]>> = {};
  /** the same values in saved-answer form, persisted so save-for-later can restore them */
  positionSeatFieldAnswers: Record<string, Record<string, AnswerModel[]>> = {};
  /** restored draft answers, resolved to attributes for the pickers to prefill from */
  private restoredSeatFieldValues: Record<string, Record<string, Attribute[]>> = {};
  /** people assigned to 2 positions will be in this list */
  invalidPositionPersonIds: string[] = [];
  /** way to trigger warning text on positions page if position page invalid */
  isTouched: boolean = false;
  hasPaymentPage: boolean = false;
  selectedIndex: number = 0;
  stepAnnouncement: string = '';
  private isHeaderButtonVisible = false;
  isSaving: boolean = false;
  isLoading: boolean = true;
  disableSubmit: boolean = false;
  displayUploadAndDeleteButtons: boolean = true;
  getFailedValidationMessage = getFailedValidationMessage; //make available in html
  uploadErrorMessage: string = '';
  formFailedValidations: FieldValidatorTracker[] = [];
  pageFailedValidations: FieldValidatorTracker[] = [];

  formPages: ApplicationFormPage[] = [];
  get showSpinner() {
    return this.isSaving || this.isLoading;
  }

  get mode(): AttributeEditorMode {
    if (this.inPreviewMode) {
      return AttributeEditorMode.Preview;
    } else {
      return AttributeEditorMode.Application;
    }
  }
  application: Application = blankApplication();

  person: PersonForMemberRequests = new PersonForMemberRequests();
  /** If true; is existing person */
  isUpdateMode: boolean = false;
  teamGroup: FormGroup<teamForm>;

  applicationForm: FormGroup<personForm>;
  isRequired: Partial<Record<FormFieldName, boolean>> = {};
  isHidden: Partial<Record<FormFieldName, boolean>> = {};
  fieldName = FormFieldName;
  addressHelper: AddressHelper;
  image: Image;
  races: Race[] = [];
  resumeCvFile: FileItem;
  errorMessage: string;
  secureLinkId: string;
  /** if has introduction text, defaults to true */
  isIntroductionVisible: boolean = true;
  attributesEditorDataSource = new AttributesEditorDataSource();
  activePage: ApplicationFormPage;
  @ViewChild('attributesEditor', { static: false }) attributesEditor: AttributesEditorComponent;
  @ViewChildren(PositionPersonPickerComponent)
  positionPickers: QueryList<PositionPersonPickerComponent>;

  get gender() {
    return Gender;
  }

  /** alias of this.application.form */
  get form(): Form {
    return this.application.form;
  }
  isPortalForm: boolean;

  private getStateCountryLookupPromise: Promise<StateCountryLookupService>;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private localPersistenceService: LocalPersistenceService,
    private applicationService: ApplicationService,
    private formBuilder: FormBuilder,
    private membersService: MembersService,
    private router: Router,
    private route: ActivatedRoute,
    private lookupService: LookupService,
    private membersResumeService: MembersResumeService,
    private appConfigService: AppConfigService,
    private buildInfoCheckService: BuildInfoCheckService,
    private snackBarService: SnackBarService,
    private addressHelperFactory: AddressHelperFactory,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
    private portalConnectionService: PortalAndLineupApplicationConnectionService,
    private accessibilityService: AccessibilityService
  ) {
    this.isPortalForm = this.appConfigService.config?.isPortal || false;
    this.getStateCountryLookupPromise = this.lookupService.getStateCountryLookup();
  }

  async stepperChange(event) {
    this.selectedIndex = event.selectedIndex;

    if (this.selectedIndex < this.formPages.length) {
      this.activePage = this.formPages[this.selectedIndex];
      const pageName = this.activePage?.name ?? '';
      this.stepAnnouncement = `Page ${this.selectedIndex + 1} of ${this.totalPages}: ${pageName}`;
      return;
    }

    //past regular pages. Either on positions page or payment page
    this.saveForLater();
    if (this.selectedIndex == this.positionsPageIndex) {
      this.stepAnnouncement = `Page ${this.totalPages} of ${this.totalPages}: Position Picker`;
      return;
    }
    if (!this.hasPaymentPage) {
      throw new Error(
        'Unexpected stepper index. Past regular steps, but not on payment or position page.'
      );
    }
    this.stepAnnouncement = `Page ${this.totalPages} of ${this.totalPages}: Payment`;
    const paymentPreview = buildPaymentPreview(
      this.createPersonDetailsWriteModel(),
      this.attributesEditorDataSource,
      this.person?.id
    );

    await this.paymentComponent.loadPayments(
      this.secureLinkId,
      this.addressHelper?.getDemographicInfoFromForm(),
      paymentPreview
    );
  }

  /** for unit tests. spying 'ngOnInit' does not work, but onInit does */
  ngOnInit() {
    this.onInit();
  }

  async onInit() {
    this.buildInfoCheckService.onReloadPrompt.subscribe(() => {
      this.saveForLater();
    });

    this.racesSubscription = this.lookupService.races.subscribe((races) => (this.races = races));

    if (this.inPreviewMode && this.previewFormId) {
      const application = await this.applicationService.getForPreview(this.previewFormId);

      this.setApplication(application);
      this.setApplicationData([], null, await this.getStateCountryLookupPromise, {});

      return;
    }
    if (!this.inPreviewMode) {
      this.isPortalForm = this.route?.snapshot?.data['isPortalForm'] === true;
    }

    this.routeSubscription = this.route.params.subscribe(async (params) => {
      let personId = params['personId'];
      this.secureLinkId = params['secureLinkId'];
      const personPromise = personId
        ? this.membersService.getPerson(personId, this.secureLinkId)
        : Promise.resolve(this.person);

      if (personId) {
        this.isUpdateMode = true;
      }
      const application = await this.applicationService.getBySecureLinkId(
        this.secureLinkId,
        personId
      );

      if (!application) {
        this.isLoading = false;
        return; //applicationService already handles bad application
      }
      this.person = await personPromise;

      this.setApplication(application);

      if (!this.person.email) {
        this.person.email = application.email; // for new person
      }
      this.image = this.person.image;
      this.applicationForm?.controls.avatar.setValue(this.image?.id ?? '');
      const stateCountyLookupService = await this.getStateCountryLookupPromise;
      const { attributes, demographicInfo, selectedPositionPersonIds } =
        this.getAttributesAndDemographicInfo(stateCountyLookupService);

      this.setApplicationData(
        attributes,
        demographicInfo,
        stateCountyLookupService,
        selectedPositionPersonIds
      );
    });
  }
  get isCreateTeam(): boolean {
    return this.form?.createTeam;
  }

  getFieldLabel(fieldName: FormFieldName, fallback: string): string {
    const field = this.form?.fields?.find((f) => f.fieldName == fieldName);
    return field?.customLabel || this.appConfigService.getPlatformLanguage(fallback);
  }
  private publicNameValidators = () => {
    if (this.isHidden.TeamPublicName) return [];

    if (this.isRequired.TeamPublicName) return [NotEmptyValidator(), Validators.maxLength(255)];

    return this.isRequired.TeamName ? [Validators.maxLength(255)] : []; //optional or read-only
  };
  /** need to wait till personObservable resolved */
  private buildForm = () => {
    if (this.isCreateTeam) {
      //Public (not private) firm forms don't require login, so avoid calling the
      //team-name-exists API as an anonymous user types the team name.
      const teamNameAsyncValidators: AsyncValidatorFn[] = this.form.isPrivate
        ? [TeamNameUniqueAsyncValidator(this.membersService)]
        : [];
      const teamNameConfig = this.isRequired.TeamName
        ? [
            '',
            [NotEmptyValidator('Team name is required'), Validators.maxLength(255)],
            teamNameAsyncValidators
          ]
        : [''];

      this.teamGroup = this.formBuilder.group({
        teamName: teamNameConfig,
        teamPublicName: ['', this.publicNameValidators()]
      });
    } else {
      this.applicationForm = this.formBuilder.group({
        firstName: ['', [NotEmptyValidator()]],
        middleName: ['', this.isRequired.MiddleName ? [NotEmptyValidator()] : []],
        lastName: ['', [NotEmptyValidator()]],
        suffix: [
          '',
          this.isRequired.Suffix
            ? [NotEmptyValidator(), Validators.maxLength(15)]
            : [Validators.maxLength(15)]
        ],
        email: [this.application.email, [NotEmptyValidator(), Validators.email]],
        prefix: [
          '',
          this.isRequired.Prefix
            ? [NotEmptyValidator(), Validators.maxLength(15)]
            : [Validators.maxLength(15)]
        ],
        phoneNumber: [
          '',
          this.isRequired.PhoneNumber
            ? [Validators.minLength(5), NotEmptyValidator()] //min 5 because you can type a single digit or '+1' and the phone validation won't kick in. But at 3 characters phone validation will kick in
            : [Validators.minLength(5)]
        ],
        phoneNumberRaw: [''],
        extension: [''],
        gender: ['', this.isRequired.Gender ? [NotEmptyValidator()] : []],
        races: [empty, this.isRequired.Race ? [NotEmptyValidator()] : []],
        jobTitle: ['', this.isRequired.JobTitle ? [NotEmptyValidator()] : []],
        company: ['', this.isRequired.Company ? [NotEmptyValidator()] : []],
        linkedInProfile: [
          '',
          this.isRequired.LinkedIn
            ? [CustomValidators.urlValidator(), NotEmptyValidator('LinkedIn Profile is required')]
            : [CustomValidators.urlValidator()]
        ],
        resumeUpload: [
          '',
          this.isRequired.Resume
            ? [NotEmptyValidator('Resume or Curriculum Vitae is required')]
            : []
        ],
        avatar: ['', this.isRequired.Avatar ? [NotEmptyValidator(photoRequiredMessage)] : []]
      });
    }
  };

  //called once
  setApplication(application: Application) {
    this.application = application;
    const hiddenAndRequired = getHiddenAndRequired(this.form.fields);
    this.isRequired = hiddenAndRequired.isRequired;
    this.isHidden = hiddenAndRequired.isHidden;

    this.buildForm();

    const { formPages, totalPages, hasPaymentPage, positionsPageIndex } =
      this.getPages(application);
    this.formPages = formPages;
    this.totalPages = totalPages;
    this.hasPaymentPage = hasPaymentPage;
    this.positionsPageIndex = positionsPageIndex;
    this.activePage = this.formPages[0];
    this.isLoading = false;

    this.setIntroductionTextVisibility();

    if (this.isSinglePagePreview) {
      this.isIntroductionVisible = false;
    }
  }

  private getPages(application: Application): {
    formPages: ApplicationFormPage[];
    totalPages: number;
    hasPaymentPage: boolean;
    positionsPageIndex: number;
  } {
    if (this.isSinglePagePreview) {
      const targetPage = application.pages.find((p) => p.id == this.previewPageId);
      if (targetPage) {
        return {
          formPages: [ApplicationFormPage.parse(targetPage)],
          hasPaymentPage: false,
          positionsPageIndex: 0,
          totalPages: 1
        };
      }
    }
    let hasPaymentPage = false;
    let positionsPageIndex = 0;
    const formPages: ApplicationFormPage[] = [];
    application.pages.forEach(function (page, i) {
      if (i == 0) {
        formPages.push(ApplicationFormPage.parse(page));
      } else if (
        application.questions &&
        application.questions.filter((x) => x.formPageId == page.id).length > 0
      ) {
        formPages.push(ApplicationFormPage.parse(page));
      }
    });

    let additionalPages = 0;
    if (application.positions?.length) {
      positionsPageIndex = formPages.length; //if 1 page. Positions is "2nd", but index 1
      additionalPages++;
    }

    if (application.form.includePaymentPage) {
      hasPaymentPage = true;
      additionalPages++;
    }

    return {
      formPages,
      hasPaymentPage,
      positionsPageIndex,
      totalPages: formPages.length + additionalPages
    };
  }

  onBackClick() {
    this.pageFailedValidations = [];
    this.formFailedValidations = [];
    this.saveForLater();
  }

  onNextClick(page: ApplicationFormPage) {
    this.attributesEditor?.validateSubFieldsInComplexFields();

    if (!page) {
      this.isFormValidMarkedIfInvalid();
      return;
    }

    //user is on a regular page (not positions or payment)

    if (this.isPageValid(page)) {
      this.pageFailedValidations = [];
      this.saveForLater();
    } else {
      this.applicationForm?.controls.avatar.markAsTouched();
      this.generatePageFailedValidatorMessageSummary(page);
    }
  }

  onStepAnimationDone() {
    this.scrollApplicationContainerToTop();
  }

  private scrollApplicationContainerToTop() {
    const scrollContainer = this.getApplicationScrollContainer();

    if (scrollContainer) {
      scrollContainer.scrollTo({ behavior: 'smooth', left: 0, top: 0 });
      return;
    }

    this.document.defaultView?.scrollTo({ behavior: 'smooth', left: 0, top: 0 });
  }

  private getApplicationScrollContainer(): HTMLElement | null {
    let element = this.elementRef.nativeElement.parentElement;
    const window = this.document.defaultView;

    while (element && window) {
      const overflowY = window.getComputedStyle(element).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return element;
      }

      element = element.parentElement;
    }

    return null;
  }

  saveAndExit() {
    this.saveForLater();
    this.portalConnectionService.backToPortal();
  }
  //called once
  public setApplicationData = (
    attributes: Attribute[] = [],
    demographicInfo: DemographicInfo,
    stateCountryLookupService: StateCountryLookupService,
    selectedPositionPersonIds: Record<string, string[]>
  ) => {
    this.cdr.detectChanges();

    this.selectedPositionPersonIds = selectedPositionPersonIds ?? {};

    this.updateApplicationForm(demographicInfo);

    this.setupApplicationQuestions(attributes);

    this.buildInfoCheckService.initBuildFileCheck("Don't worry, you won't lose your progress!");

    if (!isMailingAddressVisible(this.isHidden)) {
      return;
    }
    this.addressHelper = this.addressHelperFactory.buildSync(
      demographicInfo,
      this.getAddressValidationCacheKey(),
      this.isRequired,
      this.isHidden,
      this.form.isAddressSharedLabel,
      stateCountryLookupService
    );
  };

  private getAddressValidationCacheKey = (): string => {
    if (this.inPreviewMode) {
      return this.previewFormId;
    }

    if (this.isUpdateMode && !this.isCreateTeam) {
      //if createTeam is true do not use person.id as the cache key
      return this.person.id;
    }

    return this.secureLinkId;
  };

  /** Gets DemographicInfo from local storage or person is local storage not set*/
  private getAttributesAndDemographicInfo = (
    stateCountryLookupService: StateCountryLookupService
  ): {
    attributes: Attribute[];
    demographicInfo: DemographicInfo;
    selectedPositionPersonIds: Record<string, string[]>;
  } => {
    const localData = this.localPersistenceService.getLocalData<LocalData<PersonApplicationWrite>>(
      this.secureLinkId
    );
    if (!localData) {
      //no localstorage, so either on first load or in preview mode
      return this.isUpdateMode
        ? {
            selectedPositionPersonIds: this.person.selectedPositionPersonIds,
            attributes: this.person.customFields,
            demographicInfo: this.person.demographicInfo
          } //set from API, no local storage
        : { attributes: [], demographicInfo: null, selectedPositionPersonIds: {} }; //(preview mode): no local, no api
    }
    let attributes = [];
    let hiddenAndReadonlyQuestions = this.application.questions.filter(
      (x) => x.isReadOnly || x.isHidden
    );
    hiddenAndReadonlyQuestions.forEach((q) => {
      let attr = this.person.customFields.find((x) => x.attributeDefinitionId == q.id);
      if (attr) {
        attributes.push(attr);
      }
    });
    const loadedAttributes = localData.answers
      ? LocalPersistenceService.buildFormUsingSavedAnswers(
          localData.answers,
          this.application.questions
        )
      : [];

    attributes = attributes.concat(loadedAttributes);
    this.restoreSeatFieldAnswers(localData.positionSeatFieldAnswers);
    //set from local storage
    return {
      attributes,
      selectedPositionPersonIds: localData.reference.selectedPositionPersonIds,
      demographicInfo: stateCountryLookupService.buildDemographicInfoFromPersonApplication(
        localData.reference
      )
    };
  };

  private setupApplicationQuestions = (attributes: Attribute[]) => {
    this.attributesEditorDataSource.sortOnName = false;
    this.attributesEditorDataSource.load(
      this.application.filterForShowCurrentValues(attributes),
      this.application.questions
    );
  };

  ngOnDestroy(): void {
    if (this.routeSubscription) this.routeSubscription.unsubscribe();
    if (this.racesSubscription) this.racesSubscription.unsubscribe();
  }

  private updateApplicationForm(demographicInfo: Pick<DemographicInfo, 'gender' | 'races'>) {
    if (this.isCreateTeam) {
      this.teamGroup.patchValue({
        teamName: this.person.teamName,
        teamPublicName: this.person.teamPublicName
      });
      return;
    }
    this.applicationForm.patchValue({
      gender: demographicInfo?.gender,
      races: demographicInfo?.races ?? empty,
      firstName: this.person.firstName,
      suffix: this.person.suffix,
      prefix: this.person.prefix,
      middleName: this.person.middleName,
      lastName: this.person.lastName,
      phoneNumber: this.person.phoneNumber,
      phoneNumberRaw: cleanPhoneNumber(this.person.phoneNumber),
      extension: this.person.extension,
      jobTitle: this.person.jobTitle,
      company: this.person.company,
      linkedInProfile: this.person.linkedInProfile
    });
  }

  //result is saved in local storage
  private createPersonDetailsWriteModel = (): PersonApplicationWrite => {
    const model = new PersonApplicationWrite({
      ...this.person,
      ...this.personOrTeamFormValues,
      selectedPositionPersonIds: this.application.positions?.length
        ? this.selectedPositionPersonIds
        : undefined,
      selectedPositionSeatFieldValues: this.application.positions?.length
        ? this.selectedPositionSeatFieldValues
        : undefined,
      tags: this.person.tags,
      status: this.isUpdateMode ? this.person.status : this.application.form.newMemberDefaultStatus,
      email: this.person.email,
      secondaryEmails: this.person.secondaryEmails?.map((e) => e.email) || []
    });
    let address: DemographicInfo;

    if (this.addressHelper) {
      address = this.addressHelper.getDemographicInfoFromForm();
      model.addressValidationCacheKey = this.form.validateAddress
        ? this.addressHelper.addressValidationCacheKey
        : '';
    } else {
      address = this.person.demographicInfo;
      //application does not use address
    }
    model.setAddress(address);

    return model;
  };

  @ViewChild(ApplicationPaymentComponent, { static: false })
  paymentComponent: ApplicationPaymentComponent;
  submitApplication = async () => {
    this.attributesEditor?.validateSubFieldsInComplexFields();
    if (!this.isFormValidMarkedIfInvalid()) {
      return;
    }

    if (this.isSaving) return;

    this.isSaving = true;
    this.accessibilityService.talk('Submitting...');
    try {
      let stripeResult: PaymentResult;
      if (this.hasPaymentPage) {
        const result = await this.paymentComponent.completePayment();
        if (result.isFailed === true) {
          //payment failed. Should have done a snack bar error/console.error
          return;
        }
        stripeResult = result;
      }
      //payment successful OR no payment page
      if (this.inPreviewMode) {
        this.onPreviewSubmit();
        return; //preview mode; do no other work
      }
      await this.sendApplicationToApi(stripeResult);
    } catch (error) {
      const parsedMessage = getParsedError(error);
      if (parsedMessage.isGenericError && error.message) {
        this.snackBarService.open(error.message, false); //display error.message instead of parsedMessage.isGenericError
      } else {
        this.snackBarService.open(parsedMessage.message, false);
      }
    } finally {
      this.isSaving = false;
    }
  };

  private navigateToConfirmationPage = (submitApplicationResult: SubmitApplicationResultModel) => {
    const navState = {
      ...submitApplicationResult,
      checkInstructions: this.paymentComponent?.paymentOptions?.checkInstructions,
      confirmationTitle: this.form.confirmationTitle,
      confirmationMessage: this.form.confirmation
    };

    //the confirmation page announces itself once it has rendered. Announcing here would be
    //dropped: the region content would change while the router is tearing down this view.
    const navigationPath = this.appConfigService.config?.baseRoute
      ? `${this.appConfigService.config.baseRoute}/forms`
      : 'members'; //default

    this.router.navigate([`${navigationPath}/confirmation`], { state: navState });
  };

  public sendApplicationToApi = async (paymentResult: PaymentResult): Promise<void> => {
    const payload = buildApplicationSubmit(
      this.createPersonDetailsWriteModel(),
      this.attributesEditorDataSource,
      this.person?.id,
      paymentResult,
      this.image
    );
    const submitApplicationResult = await this.membersService
      .submitApplication(this.secureLinkId, payload)
      .then(async (submitResult) => {
        await this.uploadResumeIfNecessary(submitResult.personId);
        return submitResult;
      });

    //if exception is throw in API, will not delete local data or navigate;
    this.localPersistenceService.deleteLocalData(this.secureLinkId);
    this.navigateToConfirmationPage(submitApplicationResult);
  };

  //public for tests
  generateFailedValidatorMessageSummary() {
    this.formFailedValidations = this.getFormPageValidationErrors();
    if (this.formFailedValidations.length) {
      this.accessibilityService.talk(
        'There is a problem with the form. Please correct the following errors.'
      );
    }
  }

  private generatePageFailedValidatorMessageSummary(page: ApplicationFormPage) {
    this.pageFailedValidations = this.getFormPageValidationErrors(page);
    if (this.pageFailedValidations.length) {
      this.accessibilityService.talk(
        'There is a problem with the page. Please correct the following errors.'
      );
    }
  }

  /** if page falsy will check all Attributes */
  private getFormPageValidationErrors = (
    page: ApplicationFormPage = null
  ): FieldValidatorTracker[] =>
    [
      this.getFieldFailedValidations(),
      this.getAddressFailedValidations(),
      this.getPageQuestionsFailedValidations(page),
      this.getPositionPickerValidations()
    ].flat();

  private getPositionPickerValidations = (): FieldValidatorTracker[] => {
    if (this.positionsPageIndex === 0 || this.selectedIndex !== this.positionsPageIndex) {
      //not on positions page, or there is no positions page
      return [];
    }

    return [...this.positionErrors()];
  };

  private *positionErrors(): Generator<FieldValidatorTracker> {
    if (this.invalidPositionPersonIds?.length) {
      const invalidSet = new Set(this.invalidPositionPersonIds);
      for (const position of this.application.positions ?? []) {
        const hasDuplicate = (this.selectedPositionPersonIds[position.id] ?? []).some((id) =>
          invalidSet.has(id)
        );
        if (hasDuplicate) {
          yield new FieldValidatorTracker(
            position.id,
            position.name,
            'duplicatePerson',
            `${position.name} contains a person already assigned to another position.`
          );
        }
      }
    }
    for (const position of this.application.positions ?? []) {
      if (!position.isReadOnly && position.minSeatCount > 0) {
        const selectedCount = (this.selectedPositionPersonIds[position.id] ?? []).length;
        if (selectedCount < position.minSeatCount) {
          const personLabel = position.minSeatCount === 1 ? 'person' : 'people';
          yield new FieldValidatorTracker(
            position.id,
            position.name,
            'minSeats',
            `${position.name} requires at least ${position.minSeatCount} ${personLabel}.`
          );
        }
      }
    }
    for (const picker of this.positionPickers ?? []) {
      //required/range checks on the tracked fields come from the attribute editors themselves
      if (!picker.seatFieldsValid) {
        yield new FieldValidatorTracker(
          picker.position.id,
          picker.position.name,
          'seatFields',
          `${picker.position.name} is missing required information for one or more people.`
        );
      }
    }
  }

  private getFieldFailedValidations(): FieldValidatorTracker[] {
    var failedValidations: FieldValidatorTracker[] = [];
    const formGroup: FormGroup = <FormGroup>(this.applicationForm ?? this.teamGroup);
    Object.keys(formGroup.controls).forEach((fieldName) => {
      const control: AbstractControl = formGroup.get(fieldName);

      //races is a container of options not an input per se, use name to find it by id
      //phoneNumberRaw is an internal control used by ngx-mat-intl-tel-input. Find by id
      const controlId =
        fieldName == 'races'
          ? fieldName
          : fieldName == 'phoneNumberRaw'
            ? 'PhoneNumber'
            : fieldName == 'avatar'
              ? FormFieldName.Avatar
              : null;

      failedValidations = failedValidations.concat(
        getAllControlErrors(controlId, fieldName, control)
      );
    });

    return failedValidations;
  }

  /** if page falsy will check all attributes */
  private getPageQuestionsFailedValidations(page: ApplicationFormPage): FieldValidatorTracker[] {
    var failedValidations: FieldValidatorTracker[] = [];

    const editableAttributes: Map<string, AbstractControl> = page
      ? this.attributesEditor?.nonValidAttributesInPage(page)
      : this.attributesEditor?.nonValidAttributes();
    editableAttributes?.forEach((control: AbstractControl, controlId: string) => {
      failedValidations.push(...getAllControlErrors(controlId, null, control));
    });

    return failedValidations;
  }

  private getAddressFailedValidations = (): FieldValidatorTracker[] =>
    this.addressHelper ? this.addressHelper.getInvalidFields() : [];

  /** update validation errors when a field changes */
  updateFieldValidatorStatus = (page: ApplicationFormPage) => {
    if (this.formFailedValidations.length) {
      this.generateFailedValidatorMessageSummary();
    }
    if (this.pageFailedValidations.length) {
      this.generatePageFailedValidatorMessageSummary(page);
    }
  };

  //called when address modal closed
  addressUpdated = (page: ApplicationFormPage) => {
    this.updateFieldValidatorStatus(page);
    this.saveForLater(); //saveForLater saves address to local storage
  };

  onResumeUpload = (): ((file: FileItem) => void) => (file: FileItem) => {
    this.resumeCvFile = file;
    this.applicationForm.controls.resumeUpload.setValue(this.resumeCvFile.file.name);
  };

  onResumeClear() {
    this.resumeCvFile = null;
    this.applicationForm.controls.resumeUpload.setValue('');
  }

  noResumeUploaded() {
    return this.resumeCvFile ? false : true;
  }

  processVirusScanResults(result: DocumentScanEvent, page: ApplicationFormPage) {
    let defaultErrorMessage = 'Virus scan failed. Please try uploading the document again.';
    if (result.status == DocumentScanStatusType.Started) {
      this.disableSubmit = true;
      this.displayUploadAndDeleteButtons = false;
    } else if (result.status == DocumentScanStatusType.Finished && !result.documentPassedScan) {
      this.displayUploadAndDeleteButtons = true;
      this.disableSubmit = false;
      this.uploadErrorMessage = result.errorMessage ? result.errorMessage : defaultErrorMessage;
      this.applicationForm.controls.resumeUpload.setErrors({ documentFailedScan: true });
    } else if (result.status == DocumentScanStatusType.Finished && result.documentPassedScan) {
      this.disableSubmit = false;
      this.displayUploadAndDeleteButtons = true;
      this.updateFieldValidatorStatus(page);
    } else if (result.status == DocumentScanStatusType.RequestFailed) {
      this.displayUploadAndDeleteButtons = true;
      this.disableSubmit = false;
      this.uploadErrorMessage = result.errorMessage ? result.errorMessage : defaultErrorMessage;
      this.applicationForm.controls.resumeUpload.setErrors({ documentScanRequestFailed: true });
    }

    if (this.uploadErrorMessage != '') {
      this.updateFieldValidatorStatus(page);
    }
  }

  private async uploadResumeIfNecessary(personId: string): Promise<any> {
    if (this.resumeCvFile) {
      await this.membersResumeService.uploadResume(this.resumeCvFile, personId);
    }
  }

  private get isPersonOrTeamFormValid(): boolean {
    return this.isCreateTeam ? this.teamGroup.valid : this.applicationForm.valid;
  }

  private get personOrTeamFormValues():
    | FormGroup<teamForm>['value']
    | FormGroup<personForm>['value'] {
    return this.isCreateTeam ? this.teamGroup.value : this.applicationForm.value;
  }

  private isFormValidMarkedIfInvalid = (): boolean => {
    if (this.isFormValid()) {
      this.isTouched = false;
      return true;
    }
    this.isTouched = true;
    this.markAllAsTouched();
    this.generateFailedValidatorMessageSummary();

    return false;
  };

  onPositionPersonIdsChange(positionId: string, personIds: string[]): void {
    this.selectedPositionPersonIds[positionId] = personIds;
    this.pruneSeatFieldValues(positionId, personIds);

    const seen = new Set<string>();
    const duplicates = new Set<string>();
    Object.values(this.selectedPositionPersonIds).forEach((ids) => {
      ids.forEach((id) => {
        if (seen.has(id)) {
          duplicates.add(id);
        } else {
          seen.add(id);
        }
      });
    });
    this.invalidPositionPersonIds = [...duplicates];
    this.updateFieldValidatorStatus(null);
  }

  onPositionSeatFieldValuesChange(
    positionId: string,
    event: { personId: string; attributes: AttributeWrite[]; answers: AnswerModel[] }
  ): void {
    const forPosition = (this.selectedPositionSeatFieldValues[positionId] ??= {});
    forPosition[event.personId] = event.attributes;

    const answersForPosition = (this.positionSeatFieldAnswers[positionId] ??= {});
    answersForPosition[event.personId] = event.answers;

    //filling in a tracked field can clear a failed validation, so drop it from a summary on screen
    this.updateFieldValidatorStatus(null);
  }

  /** Drops values for anyone no longer selected, so a removed person's data isn't submitted. */
  private pruneSeatFieldValues(positionId: string, personIds: string[]): void {
    const stillSelected = new Set(personIds);
    [this.selectedPositionSeatFieldValues[positionId], this.positionSeatFieldAnswers[positionId]]
      .filter((forPosition) => !!forPosition)
      .forEach((forPosition) =>
        Object.keys(forPosition)
          .filter((personId) => !stillSelected.has(personId))
          .forEach((personId) => delete forPosition[personId])
      );
  }

  /**
   * Rebuilds saved draft answers into attributes the pickers can prefill from. Kept separate from the
   * API prefill so an unfinished draft isn't silently replaced by whatever is currently on the seat.
   */
  private restoreSeatFieldAnswers(
    saved: Record<string, Record<string, AnswerModel[]>> | undefined
  ): void {
    this.positionSeatFieldAnswers = saved ?? {};
    this.restoredSeatFieldValues = {};

    for (const position of this.application?.positions ?? []) {
      const answersByPerson = saved?.[position.id];
      if (!answersByPerson || !position.seatFields?.length) {
        continue;
      }

      this.restoredSeatFieldValues[position.id] = Object.fromEntries(
        Object.entries(answersByPerson).map(([personId, answers]) => [
          personId,
          LocalPersistenceService.buildFormUsingSavedAnswers(answers ?? [], position.seatFields)
        ])
      );
    }
  }

  seatFieldValuesForPosition(positionId: string): Record<string, Attribute[]> {
    //a saved draft wins over what's currently on the seat — it's the applicant's newer work
    return (
      this.restoredSeatFieldValues[positionId] ??
      this.person?.selectedPositionSeatFieldValues?.[positionId] ??
      noSeatFieldValues
    );
  }

  isFormValid = () => {
    if (!this.attributesEditorDataSource.isValid) return false;
    if (!this.isPersonOrTeamFormValid) return false;
    if (this.addressHelper && !this.addressHelper.valid) return false;
    if (this.application.positions?.length) {
      //odd syntax, but if allows aborting early once a single validation error is found.
      const allRequiredFilled = this.positionErrors().next().done;
      return allRequiredFilled;
    }
    return true;
  };

  isPageValid = (page: ApplicationFormPage): boolean => {
    if (!page.isValid) return false;
    if (!this.isPersonOrTeamFormValid) return false;
    return this.addressHelper ? this.addressHelper.valid : true; //address might be turned off
  };

  imageChangedCallbackReference = (page: ApplicationFormPage) => (image_id: string) => {
    this.image = new Image(image_id);
    const avatar = this.applicationForm?.controls.avatar;
    avatar?.setValue(image_id ?? '');
    avatar?.markAsTouched();
    this.errorMessage = null;
    this.updateFieldValidatorStatus(page);
  };

  /** A photo upload has no input to blur, so the button's blur stands in for it. */
  onAvatarBlur() {
    this.applicationForm?.controls.avatar.markAsTouched();
  }

  get showAvatarRequiredError(): boolean {
    const avatar = this.applicationForm?.controls.avatar;
    return !!avatar && avatar.touched && avatar.invalid;
  }

  imageErrorCallbackReference = () => (error_message: string) => {
    this.errorMessage = error_message;
  };

  updateRace(race, event) {
    let personRaces = this.getPersonRaces();

    if (event.checked) {
      personRaces.push(race);
    } else {
      let unselected = _.find(personRaces, function (currentRace) {
        return currentRace.id == race.id;
      });

      _.remove(personRaces, function (race: Race) {
        return race.id == unselected.id;
      });
    }

    this.setPersonRaces(_.sortBy(personRaces, (r) => r.name));
  }

  getRaceCheckboxStatus(race) {
    return !!this.getPersonRaces().find((r) => r.id == race.id);
  }

  private getPersonRaces() {
    return this.applicationForm.controls.races.value;
  }

  private setPersonRaces(races) {
    this.applicationForm.controls.races.setValue(races);
  }

  private setIntroductionTextVisibility() {
    if (this.application && this.form && this.form.introduction) {
      return; //no work to do. Default is visible
    }
    this.isIntroductionVisible = false;
    this.setupHeaderButton();
  }

  isAppLoaded(): boolean {
    return this.form && !!this.form.id;
  }

  get isResumeVisible(): boolean {
    return !this.isHidden.Resume && this.appConfigService.tenant.plan.canUploadDocuments;
  }

  isGenderSelectable(gender: string): boolean {
    const genderField = this.application.form.fields.find(
      (f) => f.fieldName.toString() == ApplicationFieldName.Gender
    );

    if (!genderField) return false;

    // while male and female are forced true the default here has to be false because we don't
    // know if the other values are excluded on purpose or just not configured
    if (!genderField.valueList || genderField.valueList.length == 0) return false;

    return genderField.valueList.findIndex((v) => v.itemValue == gender) >= 0;
  }

  get isProfessionalInformationVisible(): boolean {
    return (
      !this.isHidden.JobTitle ||
      !this.isHidden.Company ||
      !this.isHidden.LinkedIn ||
      this.isResumeVisible
    );
  }

  startApplication() {
    this.isIntroductionVisible = false;
    this.setupHeaderButton();
    const firstPage = this.formPages[0];
    const pageName = firstPage?.name ?? '';
    this.stepAnnouncement = `Page 1 of ${this.totalPages}: ${pageName}`;
    if (window) {
      window.scroll({
        top: 0,
        left: 0
      });
    }
  }

  downloadFile(event: { fileName: string; attributeDefinitionId: string }) {
    let requestUrl = `${this.secureLinkId}/${event.attributeDefinitionId}/document`;
    this.membersService.downloadFile(requestUrl, event.fileName);
  }

  addEmailFn = (email: string): Promise<boolean> => {
    // Spread into a new array instead of using .push() so Angular detects the reference change
    // and triggers ngOnChanges in EmailEditorComponent to re-render the email list.
    this.person.secondaryEmails = [
      ...(this.person.secondaryEmails ?? []),
      { email: email, isVerified: true, id: email }
    ]; //add to local person model immediately for responsive UI, the id is temporary and only used for display purposes until we get the real email object back from the server on next person details fetch. We also set isVerified to true immediately so that the email shows up as verified in the UI, since we don't have any concept of unverified emails in our current system and verification is not required.

    return Promise.resolve(true);
  };

  makePrimaryFn = (email: PersonEmailModel): Promise<boolean> => {
    this.person.secondaryEmails = this.person.secondaryEmails.filter((e) => e.id !== email.id); //remove the new primary email from the emails list in local person model immediately for responsive UI, it will be added back as primary email on next person details fetch. We also update the form control value to update the primary email in the email editor UI.
    if (this.person.email && this.applicationForm.controls.email.valid) {
      this.person.secondaryEmails = [
        ...this.person.secondaryEmails,
        { email: this.person.email, isVerified: true, id: this.person.email }
      ]; //add the old primary email to the emails list in local person model immediately for responsive UI, the id is temporary and only used for display purposes until we get the real email object back from the server on next person details fetch. We also set isVerified to true immediately so that the email shows up as verified in the UI, since we don't have any concept of unverified emails in our current system and verification is not required.
    }
    this.person.email = email.email; //update primary email in local person model immediately for responsive UI
    this.applicationForm.controls.email.setValue(email.email);
    return Promise.resolve(true);
  };

  deleteEmailFn = (emailId: string): Promise<boolean> => {
    const emailIndex = this.person.secondaryEmails.findIndex((e) => e.id === emailId);
    if (emailIndex !== -1) {
      this.person.secondaryEmails = this.person.secondaryEmails.filter((e) => e.id !== emailId); //remove from local person model immediately for responsive UI
    }
    return Promise.resolve(true);
  };
  private markAllAsTouched() {
    this.applicationForm?.markAllAsTouched();
    this.teamGroup?.markAllAsTouched();
    this.attributesEditor?.markAllAsTouched();
    this.addressHelper?.addressFormGroup?.markAllAsTouched();
  }

  private setupHeaderButton() {
    if (this.inPreviewMode) return;
    if (this.isHeaderButtonVisible) return;

    this.localPersistenceService.buttonSubject.next({
      label: 'Save For Later',
      action: () => this.saveForLater(true),
      tooltip:
        'Your responses will be saved for 15 days. You must use the same device and browser when ' +
        "coming back. You must still submit responses once you're ready."
    });
    this.isHeaderButtonVisible = true;
  }

  private saveForLater = (showMessage: boolean = false) =>
    this.localPersistenceService.saveDataForLater(
      this.buildLocalData(),
      this.secureLinkId,
      showMessage
    );

  private buildLocalData = (): LocalData<PersonApplicationWrite> => ({
    reference: this.createPersonDetailsWriteModel(),
    answers: this.localPersistenceService.getAnswers(this.attributesEditorDataSource),
    positionSeatFieldAnswers: this.positionSeatFieldAnswers
  });
}
